/**
 * Approved timesheet corrections that the rebuild could not use.
 *
 * Paul Benson's ruling, 2 Nov 2026: "We can't hold up this pay run based on
 * additional confirmations." Where an approved correction fails its checks but
 * the approved timesheet itself can be priced, the timesheet is paid as
 * approved and each of its pay lines carries one of these warnings, which the
 * pay run lists as a named review item. Only when the approved timesheet can't
 * be priced either is that one source held, with CORRECTION_HOLD_CODE.
 */
export const CORRECTION_HOLD_CODE = 'pricing_frozen_correction_invalid';

export const CORRECTION_NOT_APPLIED_PREFIX = 'pricing_correction_not_applied_';

export type CorrectionNotAppliedCategory =
  | 'review_evidence'
  | 'corrected_times'
  | 'allowances'
  | 'allowance_total'
  | 'leave_missing'
  | 'duplicate'
  | 'other';

const CATEGORY_BY_REASON: Record<string, CorrectionNotAppliedCategory> = {
  checker_review_evidence: 'review_evidence',
  external_review_evidence: 'review_evidence',
  review_method: 'review_evidence',
  effective_context: 'corrected_times',
  allowance_breakdown: 'allowances',
  allowance_line: 'allowances',
  allowance_provenance: 'allowances',
  allowance_numeric: 'allowances',
  allowance_duplicate: 'allowances',
  allowance_contract: 'allowances',
  allowance_amount: 'allowances',
  allowance_total: 'allowances',
  handover_rolling_cap: 'allowances',
  superseded_allowance_amount: 'allowance_total',
  supersession_total: 'allowance_total',
  leave_request_missing: 'leave_missing',
  multiple_approved_rows: 'duplicate',
};

/** The persisted pricing warning for a validator reason token. */
export function correctionNotAppliedWarning(reason: string): string {
  return `${CORRECTION_NOT_APPLIED_PREFIX}${CATEGORY_BY_REASON[reason] ?? 'other'}`;
}

export function correctionNotAppliedCategory(warning: string): CorrectionNotAppliedCategory | null {
  if (!warning.startsWith(CORRECTION_NOT_APPLIED_PREFIX)) return null;
  const category = warning.slice(CORRECTION_NOT_APPLIED_PREFIX.length);
  return (Object.values(CATEGORY_BY_REASON) as string[]).includes(category) || category === 'other'
    ? (category as CorrectionNotAppliedCategory)
    : null;
}

/** First correction warning on a line's persisted pricing warnings, if any. */
export function correctionNotAppliedFrom(warnings: unknown): string | null {
  if (!Array.isArray(warnings)) return null;
  for (const warning of warnings)
    if (typeof warning === 'string' && correctionNotAppliedCategory(warning)) return warning;
  return null;
}
